import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAcceptJs } from 'react-acceptjs';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from '../../Hooks/useAxiosPrivate';
import { useUser } from '../../Hooks/useUser';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const OrderPayment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const fromRegistration = location.state?.fromRegistration || false;
  const isAddition = location.state?.isAddition || false;
  const regState = location.state || {};
  const registration = regState?.registration || {};
  const session = useUser();

  const authData = useMemo(() => ({
    apiLoginID: process.env.REACT_APP_apiLoginID || '',
    clientKey:  process.env.REACT_APP_clientKey  || '',
  }), []);

  const { dispatchData, loading: authNetLoading } = useAcceptJs({
    environment: 'PRODUCTION',
    authData,
  });

  // ── Payment info ──────────────────────────────────────────────────────────
  const [paymentInfo, setPaymentInfo]   = useState(null);
  const [isLoading,   setIsLoading]     = useState(true);
  const [error,       setError]         = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState('full');

  // ── Deposit calculation ───────────────────────────────────────────────────
  // allResults has one entry per attendee created in this registration, so
  // quantity is already expanded. Each attendee's registration contributes its
  // own deposit amount (if it allows one) and the order deposit is the sum.
  // Falls back to the single registration when allResults isn't available.
  const registrationsForDeposit = Array.isArray(regState.allResults) && regState.allResults.length > 0
    ? regState.allResults.map((r) => r?.registration).filter(Boolean)
    : (registration && Object.keys(registration).length > 0 ? [registration] : []);

  const depositRegistrations = registrationsForDeposit.filter(
    (r) => Boolean(r?.RegistrationAllowDeposit) && Number(r?.RegistrationDepositAmount) > 0
  );

  const orderBalance = Number(paymentInfo?.order?.Balance ?? 0);

  const rawDepositTotal = depositRegistrations.reduce(
    (sum, r) => sum + Number(r.RegistrationDepositAmount),
    0
  );

  // Never ask for more than the balance.
  const depositAmount = round2(Math.min(rawDepositTotal, orderBalance));

  const dueDayValues = depositRegistrations
    .map((r) => r?.RegistrationDepositDueDays)
    .filter((d) => d !== null && d !== undefined && d !== '')
    .map(Number)
    .filter((d) => Number.isFinite(d));
  const depositDueDays = dueDayValues.length > 0 ? Math.min(...dueDayValues) : null;

  const depositAllowed =
    depositRegistrations.length > 0 &&
    depositAmount > 0 &&
    orderBalance > depositAmount &&
    !paymentInfo?.order?.DepositPaid;

  // ── Form state ────────────────────────────────────────────────────────────
  const [amount, setAmount] = useState('');
  const [billing, setBilling] = useState({
    firstName: '', lastName: '', address: '', apt: '',
    city: '', state: 'NC', zip: '',
  });
  const [cardData, setCardData] = useState({
    cardNumber: '', nameOnCard: '', expMonth: '', expYear: '', cardCode: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [expDisplay, setExpDisplay] = useState('');

  // ── Fetch payment info ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        setIsLoading(true);

        const [orderRes, orderItemRes] = await Promise.all([
          axiosPrivate.get(`/api/orders/${orderId}`),
          axiosPrivate.get(`/api/orders/${orderId}/items`),
        ]);

        setPaymentInfo({
          ...orderRes.data,
          orderItems: orderItemRes.data,   // merge items onto the same object
        });

        //console.log('Payment Info: ', orderRes.data, orderItemRes.data);

        setError(null);
      } catch (err) {
        console.error('Error fetching payment info:', err);
        const msg = err?.response?.data?.error || 'Failed to load payment information';
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInfo();
  }, [orderId]);

  // Auto-fill billing from user profile
  useEffect(() => {
    if (!paymentInfo) return;
    const uid = session?.user?.UserID;
    if (!uid) return;

    const firstName = session?.user?.FirstName ||
      (session?.user?.FullName ? session.user.FullName.split(' ')[0] : '') || '';
    const lastName = session?.user?.LastName ||
      (session?.user?.FullName ? session.user.FullName.split(' ').slice(1).join(' ') : '') || '';

    let cancelled = false;
    axiosPrivate
      .get(`/api/users/usersettings/${uid}`)
      .then((resp) => {
        if (cancelled) return;
        const s = resp.data;
        setBilling((prev) => ({
          ...prev,
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
          ...(s && {
            address: prev.address || s.BP_G_Address || '',
            apt: prev.apt || s.BP_G_AddressLine2 || '',
            city: prev.city || s.BP_G_AddressCity || '',
            state: (prev.state && prev.state !== 'NC') ? prev.state : (s.BP_G_AddressState || 'NC'),
            zip: prev.zip || s.BP_G_AddressZip || '',
          }),
        }));
        setCardData((prev) => {
          if (prev.nameOnCard) return prev;
          const full = `${firstName} ${lastName}`.trim();
          return full ? { ...prev, nameOnCard: full } : prev;
        });
      })
      .catch(() => {
        setBilling((prev) => ({
          ...prev,
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
        }));
        setCardData((prev) => {
          if (prev.nameOnCard) return prev;
          const full = `${firstName} ${lastName}`.trim();
          return full ? { ...prev, nameOnCard: full } : prev;
        });
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo, session?.user?.UserID]);

  // Pre-fill amount once paymentInfo is ready
  useEffect(() => {
    if (!paymentInfo?.order) return;

    if (paymentType === 'deposit' && depositAllowed) {
      setAmount(depositAmount.toFixed(2));
    } else {
      setAmount(Number(paymentInfo.order.Balance).toFixed(2));
    }
  }, [paymentInfo, paymentType, depositAllowed, depositAmount]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0)
      errors.amount = 'Please enter a valid amount';
    else if (amountNum > paymentInfo?.order?.Balance)
      errors.amount = 'Amount cannot exceed balance';

    if (
      paymentType === 'deposit' &&
      depositAllowed &&
      amountNum < depositAmount
    ) {
      errors.amount = `Minimum deposit amount is $${depositAmount.toFixed(2)}`;
    }

    if (!billing.firstName.trim()) errors.firstName = 'First name is required';
    if (!billing.lastName.trim())  errors.lastName  = 'Last name is required';
    if (!billing.address.trim())   errors.address   = 'Address is required';
    if (!billing.city.trim())      errors.city      = 'City is required';
    if (!billing.state)            errors.state     = 'State is required';
    if (!billing.zip.trim())       errors.zip       = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(billing.zip.trim()))
      errors.zip = 'Invalid ZIP code format';

    if (!cardData.cardNumber.trim())
      errors.cardNumber = 'Card number is required';
    else if (!/^\d{13,19}$/.test(cardData.cardNumber.replace(/\s/g, '')))
      errors.cardNumber = 'Invalid card number';

    if (!cardData.nameOnCard.trim()) errors.nameOnCard = 'Name on card is required';

    if (!cardData.expMonth || !cardData.expYear)
      errors.expiration = 'Expiration date is required';
    else {
      if (parseInt(cardData.expMonth) < 1 || parseInt(cardData.expMonth) > 12)
        errors.expiration = 'Invalid month';
      if (parseInt(cardData.expYear) < 0 || parseInt(cardData.expYear) > 99)
        errors.expiration = 'Invalid year';
    }

    if (!cardData.cardCode.trim())
      errors.cardCode = 'Security code is required';
    else if (!/^\d{3,4}$/.test(cardData.cardCode))
      errors.cardCode = 'Invalid security code';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  //submit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!authData.apiLoginID || !authData.clientKey) {
      enqueueSnackbar('Payment is not configured (missing Authorize.net keys).', { variant: 'error' });
      return;
    }
    if (!validateForm()) {
      enqueueSnackbar('Please fix form errors', { variant: 'error' });
      return;
    }
    if (!orderId) {
      enqueueSnackbar('No order found for this payment.', { variant: 'error' });
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: tokenize card with Authorize.net Accept.js — card data never touches your server
      const authResponse = await dispatchData({
        cardData: {
          cardNumber: cardData.cardNumber.replace(/\s/g, ''),
          month:      cardData.expMonth.padStart(2, '0'),
          year:       cardData.expYear.padStart(2, '0'),
          cardCode:   cardData.cardCode,
        },
      });

      if (authResponse.messages.resultCode === 'Error') {
        const msg = authResponse.messages.message?.[0]?.text || 'Tokenization failed';
        enqueueSnackbar(msg, { variant: 'error' });
        setIsProcessing(false);
        return;
      }

      // Step 2: build billing payload with card metadata for transaction record
      const opaqueData   = authResponse.opaqueData;
      const cardNum      = cardData.cardNumber.replace(/\s/g, '');
      const cardLastFour = cardNum.slice(-4);
      let cardType = 'Unknown';
      if      (cardNum.startsWith('4'))                            cardType = 'Visa';
      else if (cardNum.startsWith('5') || cardNum.startsWith('2')) cardType = 'Mastercard';
      else if (cardNum.startsWith('3'))                            cardType = 'Amex';
      else if (cardNum.startsWith('6'))                            cardType = 'Discover';

      const billingPayload = { ...billing, cardLastFour, cardType };

      // Step 3: send opaque token + billing to your order payment route
      const { data: paymentResult } = await axiosPrivate.post(
        `/api/orders/${orderId}/process-payment`,
        {
          amount:      parseFloat(amount),
          opaqueData,
          billing:     billingPayload,
        }
      );

      enqueueSnackbar('Payment processed successfully!', { variant: 'success' });

      setTimeout(() => {
        navigate('/events/thanks', {
          state: {
            event:            regState.event,
            attendee:         regState.attendee,
            attendees:        regState.attendees,
            registration:     regState.registration,
            order:            paymentResult.order ?? regState.order,
            allResults:       regState.allResults,
            paymentCompleted: true,
            amountPaid:       paymentResult.amountPaid,
            newBalance:       paymentResult.newBalance,
            transactionId:    paymentResult.transactionId,
            isAddition:       isAddition
          },
        });
      }, 1000);

    } catch (err) {
      console.error('Payment error:', err);

      // Accept.js throws a non-Axios error shape — check for it first
      let msg = 'Payment processing failed';
      if (err?.messages?.message?.[0]) {
        const e = err.messages.message[0];
        msg = `${e.text} (Code: ${e.code})`;
      } else if (err?.response?.data?.error) {
        msg = err.response.data.error;
      }

      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Shared header ─────────────────────────────────────────────────────────
  const Header = () => (
    <div className="full-row light-blue">
      <div className="container-fluid">
        <div className="row text-white pt-3 pb-3">
          <div className="col-md-12">
            <h1 className="text-uppercase">MAKE PAYMENT</h1>
            <p>Pay on outstanding balances</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) return (
    <main>
      <Header />
      <div className="full-row white">
        <div className="container-fluid">
          <div className="row mt-5">
            <div className="col-md-12 text-center"><p>Loading payment information...</p></div>
          </div>
        </div>
      </div>
    </main>
  );

  if (error || !paymentInfo) return (
    <main>
      <Header />
      <div className="full-row white">
        <div className="container-fluid">
          <div className="row mt-5">
            <div className="col-md-12 text-center">
              <p style={{ color: '#d32f2f' }}>{error || 'Payment information not available'}</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );

  return (
    <main>
      <Header />

      <form onSubmit={handleSubmit}>

        {/* ── Order summary & amount ──────────────────────────────────── */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 mb-3">
                <h3>
                  {(paymentInfo.eventCode && paymentInfo.eventTitle?.startsWith(paymentInfo.eventCode)
                    ? paymentInfo.eventTitle
                    : [paymentInfo.eventCode, paymentInfo.eventTitle].filter(Boolean).join(' ')).trim()} has a balance of{' '}
                  <span className="required-field">
                    ${parseFloat(paymentInfo?.order?.Balance || 0).toFixed(2)}
                  </span>
                </h3>
              </div>

              {/* Payment Type Selection */}
              <div className="col-md-12 mb-4">
                {depositAllowed ? (
                  <>
                    <label className="mb-2 d-block" style={{ fontWeight: 600 }}>
                      Payment Option
                    </label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

                      {/* Full Payment Option */}
                      <label
                        style={{
                          flex: 1,
                          minWidth: 220,
                          border: `2px solid ${paymentType === 'full' ? '#003063' : '#dee2e6'}`,
                          borderRadius: 8,
                          padding: '14px 18px',
                          cursor: 'pointer',
                          backgroundColor: paymentType === 'full' ? '#f0f4ff' : '#fff',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentType"
                          value="full"
                          checked={paymentType === 'full'}
                          onChange={() => setPaymentType('full')}
                          style={{ marginRight: 10 }}
                        />
                        <span style={{ fontWeight: 600 }}>Pay Full Balance</span>
                        <div style={{ marginTop: 4, color: '#555', fontSize: '0.875rem' }}>
                          ${Number(paymentInfo.order.Balance).toFixed(2)} — clears your balance completely
                        </div>
                      </label>

                      {/* Deposit Option */}
                      <label
                        style={{
                          flex: 1,
                          minWidth: 220,
                          border: `2px solid ${paymentType === 'deposit' ? '#003063' : '#dee2e6'}`,
                          borderRadius: 8,
                          padding: '14px 18px',
                          cursor: 'pointer',
                          backgroundColor: paymentType === 'deposit' ? '#f0f4ff' : '#fff',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentType"
                          value="deposit"
                          checked={paymentType === 'deposit'}
                          onChange={() => setPaymentType('deposit')}
                          style={{ marginRight: 10 }}
                        />
                        <span style={{ fontWeight: 600 }}>Pay Deposit</span>
                        <div style={{ marginTop: 4, color: '#555', fontSize: '0.875rem' }}>
                          ${depositAmount.toFixed(2)}
                          {depositRegistrations.length > 1
                            ? ` — total deposit for ${depositRegistrations.length} registrations`
                            : ''}
                        </div>
                      </label>

                    </div>
                  </>
                ) : (
                  <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: 0 }}>
                    Full balance payment required for this registration.
                  </p>
                )}
              </div>

              {/* Order line-item breakdown */}
              {paymentInfo.orderItems?.length > 0 && (
                <div className="col-md-12 mb-4">
                  <h5 className="mb-3">Order Items</h5>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Description</th>
                          <th scope="col" className="text-center" style={{ width: 100 }}>Qty</th>
                          <th scope="col" className="text-end" style={{ width: 160 }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentInfo.orderItems.map((item, i) => (
                          <tr key={item.AttendeeID || i}>
                            <td>{item.Description || '—'}</td>
                            <td className="text-center">{item.Quantity ?? 1}</td>
                            <td className="text-end">
                              ${parseFloat(item.TotalPrice || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2} className="text-end">Total Due</th>
                          <th className="text-end">
                            ${parseFloat(paymentInfo?.order?.Balance || 0).toFixed(2)}
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              <div className="col-md-6 mb-3">
                <label className="mb-2">
                  {depositAllowed
                    ? 'Amount to Pay'
                    : 'Enter Amount to Pay'}
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                {validationErrors.amount && (
                  <small style={{ color: '#d32f2f' }}>{validationErrors.amount}</small>
                )}
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* ── Billing ────────────────────────────────────────────────── */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5 mb-5">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>First Name</label>
                  <input type="text" maxLength="200" className="form-control" placeholder="First name"
                    value={billing.firstName} onChange={(e) => setBilling({ ...billing, firstName: e.target.value })} />
                  {validationErrors.firstName && <small style={{ color: '#d32f2f' }}>{validationErrors.firstName}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>Last Name</label>
                  <input type="text" maxLength="200" className="form-control" placeholder="Last name"
                    value={billing.lastName} onChange={(e) => setBilling({ ...billing, lastName: e.target.value })} />
                  {validationErrors.lastName && <small style={{ color: '#d32f2f' }}>{validationErrors.lastName}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>Address</label>
                  <input type="text" maxLength="200" className="form-control" placeholder="Address"
                    value={billing.address} onChange={(e) => setBilling({ ...billing, address: e.target.value })} />
                  {validationErrors.address && <small style={{ color: '#d32f2f' }}>{validationErrors.address}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <input type="text" maxLength="200" className="form-control" placeholder="Apartment, suite, etc. (optional)"
                    value={billing.apt} onChange={(e) => setBilling({ ...billing, apt: e.target.value })} />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>City</label>
                  <input type="text" maxLength="200" className="form-control" placeholder="City"
                    value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} />
                  {validationErrors.city && <small style={{ color: '#d32f2f' }}>{validationErrors.city}</small>}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">State</label>
                  <select className="DropDownField form-control" value={billing.state}
                    onChange={(e) => setBilling({ ...billing, state: e.target.value })}>
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option><option value="AK">Alaska</option><option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option><option value="CA">California</option><option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option><option value="DE">Delaware</option><option value="DC">District of Columbia</option>
                    <option value="FL">Florida</option><option value="GA">Georgia</option><option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option><option value="IL">Illinois</option><option value="IN">Indiana</option>
                    <option value="IA">Iowa</option><option value="KS">Kansas</option><option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option><option value="ME">Maine</option><option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option><option value="MI">Michigan</option><option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option><option value="MO">Missouri</option><option value="MT">Montana</option>
                    <option value="NE">Nebraska</option><option value="NV">Nevada</option><option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option><option value="NM">New Mexico</option><option value="NY">New York</option>
                    <option value="NC">North Carolina</option><option value="ND">North Dakota</option><option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option><option value="OR">Oregon</option><option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option><option value="SC">South Carolina</option><option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option><option value="TX">Texas</option><option value="UT">Utah</option>
                    <option value="VT">Vermont</option><option value="VA">Virginia</option><option value="WA">Washington</option>
                    <option value="WV">West Virginia</option><option value="WI">Wisconsin</option><option value="WY">Wyoming</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>ZIP Code</label>
                  <input type="text" maxLength="10" className="form-control" placeholder="ZIP Code"
                    value={billing.zip} onChange={(e) => setBilling({ ...billing, zip: e.target.value })} />
                  {validationErrors.zip && <small style={{ color: '#d32f2f' }}>{validationErrors.zip}</small>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* ── Credit card ─────────────────────────────────────────────── */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5 mb-0">
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <div className="row align-items-center mb-2">
                    <div className="col-md-6">
                      <label className="required-caption mb-0"><span className="required-field">*</span>Credit Card</label>
                    </div>
                    <div className="col-md-6">
                      <ul id="credit-cards">
                        <li><i className="fa-brands fa-cc-visa" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-mastercard" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-discover" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-amex" aria-hidden="true"></i></li>
                      </ul>
                    </div>
                  </div>
                  <input type="text" maxLength="19" className="form-control"
                    value={cardData.cardNumber} onChange={(e) => setCardData({ ...cardData, cardNumber: e.target.value })} />
                  {validationErrors.cardNumber && <small style={{ color: '#d32f2f' }}>{validationErrors.cardNumber}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>Name on Card</label>
                  <input type="text" maxLength="200" className="form-control"
                    value={cardData.nameOnCard} onChange={(e) => setCardData({ ...cardData, nameOnCard: e.target.value })} />
                  {validationErrors.nameOnCard && <small style={{ color: '#d32f2f' }}>{validationErrors.nameOnCard}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>Expiration Date</label>
                  <input
                    type="text"
                    maxLength="5"
                    className="form-control"
                    placeholder="MM/YY"
                    value={expDisplay}
                    onChange={(e) => {
                      // Strip everything except digits
                      const digits = e.target.value.replace(/\D/g, '');

                      // Auto-insert slash after MM
                      let formatted = digits;
                      if (digits.length > 2) {
                        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
                      }

                      setExpDisplay(formatted);
                      setCardData({
                        ...cardData,
                        expMonth: digits.slice(0, 2),
                        expYear:  digits.slice(2, 4),
                      });
                    }}
                  />
                  {validationErrors.expiration && <small style={{ color: '#d32f2f' }}>{validationErrors.expiration}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2"><span className="required-field">*</span>Security Code</label>
                  <input type="text" maxLength="4" className="form-control"
                    value={cardData.cardCode} onChange={(e) => setCardData({ ...cardData, cardCode: e.target.value })} />
                  {validationErrors.cardCode && <small style={{ color: '#d32f2f' }}>{validationErrors.cardCode}</small>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* ── Submit ──────────────────────────────────────────────────── */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row my-3">
              <div className="col-md-12 text-center">
                <button type="submit" className="button-default" disabled={isProcessing || authNetLoading}>
                  {isProcessing ? 'PROCESSING...' : 'MAKE PAYMENT'}
                </button>
              </div>
            </div>

            {fromRegistration && Boolean(regState?.event?.PayLater) && (
              <div className="row mb-3">
                <div className="col-md-12 text-center">
                  <p style={{ color: '#666', margin: '8px 0 12px' }}>
                    Not ready to pay now? You can pay later by mail or phone.
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/events/thanks', {
                      state: {
                        event:        regState.event,
                        attendee:     regState.attendee,
                        attendees:    regState.attendees,
                        registration: regState.registration,
                        order:        regState.order,
                        allResults:   regState.allResults,
                        payLater:     true,
                        isAddition:   isAddition
                      },
                    })}
                  >
                    PAY LATER
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </form>
    </main>
  );
};

export default OrderPayment;